// Page chrome: the nav's scrolled state, the one-shot section reveals, and the lazy
// fetch of the app embed. A few hundred bytes, no React, and nothing here is needed
// for the page to read or for any link to work.

export function initChrome() {
  // ── nav: transparent until 24px of scroll, then frosted (handoff §4.1) ──
  // Coalesced into one rAF and guarded on the value actually changing. The previous
  // version wrote to classList on every scroll event, which meant a style
  // invalidation per event rather than per state change.
  const header = document.querySelector('[data-nav]')
  if (header) {
    let solid = null
    let queued = false
    const apply = () => {
      queued = false
      const next = window.scrollY > 24
      if (next === solid) return
      solid = next
      header.classList.toggle('is-solid', next)
    }
    apply()
    addEventListener('scroll', () => {
      if (queued) return
      queued = true
      requestAnimationFrame(apply)
    }, { passive: true })
  }

  // ── section reveals ──
  // `data-armed` is what makes the element hidden, and it is set here — one statement
  // before the observer takes responsibility for un-hiding it. Nothing is ever hidden
  // by a script that has not yet reached this line, so a failure upstream degrades to
  // "no animation" rather than "no content". Reduced motion resolves to the final
  // state in CSS.
  const revealed = new IntersectionObserver((entries, io) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      entry.target.setAttribute('data-shown', '1')
      io.unobserve(entry.target)
    }
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 })

  for (const el of document.querySelectorAll('[data-reveal]')) {
    el.setAttribute('data-armed', '1')
    revealed.observe(el)
  }

  // ── app embed, fetched on approach ──
  // Vite code-splits this dynamic import into its own chunk, so React and the
  // prototype never sit on the critical path. A failed fetch is survivable: the
  // slots keep their placeholders, which are designed to be presentable.
  const slots = document.querySelectorAll('[data-island]')
  if (!slots.length) return

  let requested = false
  const approach = new IntersectionObserver((entries, io) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      io.disconnect()
      if (requested) return
      requested = true
      import('./islands.jsx')
        .then((m) => m.mountIslands())
        .catch((error) => console.warn('[paraplu] app embed unavailable', error))
      return
    }
  }, { rootMargin: '400px 0px' })

  for (const slot of slots) approach.observe(slot)
}
